import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  getBusinessLocations,
  saveSelectedLocation
} from '../../services/googleOAuth';
import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../ui/card';
import { Building2, MapPin, Phone, Globe, Check } from 'lucide-react';

const LocationSelector = ({ clientId, onComplete, onSkip }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, [clientId]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getBusinessLocations(
        process.env.REACT_APP_API_OAUTH_GET_LOCATIONS,
        clientId
      );

      if (response.success) {
        setLocations(response.locations || []);

        if (response.locations.length === 0) {
          setError('No locations found for this Google Business account.');
        }
      } else {
        setError(response.error || 'Failed to fetch locations');
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('An error occurred while fetching locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = async (location) => {
    try {
      setSaving(true);
      setError(null);

      const response = await saveSelectedLocation(
        process.env.REACT_APP_API_OAUTH_SAVE_LOCATION,
        clientId,
        location
      );

      if (response.success) {
        setSelectedLocation(location);
        // Call onComplete after a short delay to show success state
        setTimeout(() => {
          onComplete(location);
        }, 1500);
      } else {
        setError(response.error || 'Failed to save location');
        setSaving(false);
      }
    } catch (err) {
      console.error('Error saving location:', err);
      setError('An error occurred while saving the location');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            Loading your Google Business locations...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-red-600 text-5xl mb-4">⚠️</div>
              <p className="text-gray-700 mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={fetchLocations} variant="outline">
                  Try Again
                </Button>
                <Button onClick={onSkip} variant="ghost">
                  Skip for Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">
          Select Your Business Location
        </h2>
        <p className="text-gray-600">
          Choose which Google Business location you want to manage from this
          platform.
        </p>
      </div>

      {locations.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                No business locations found in your Google Business Profile.
              </p>
              <Button onClick={onSkip} variant="outline">
                Skip for Now
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {locations.map((location) => (
            <Card
              key={location.name}
              className={`cursor-pointer transition-all ${
                selectedLocation?.name === location.name
                  ? 'ring-2 ring-green-500 bg-green-50'
                  : saving
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:shadow-lg'
              }`}
              onClick={() => !saving && handleSelectLocation(location)}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold">
                        {location.title}
                      </h3>
                      {selectedLocation?.name === location.name && (
                        <div className="ml-auto flex items-center gap-2 text-green-600">
                          <Check className="h-5 w-5" />
                          <span className="text-sm font-medium">Selected</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      {location.address && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{location.address}</span>
                        </div>
                      )}

                      {location.phoneNumber && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span>{location.phoneNumber}</span>
                        </div>
                      )}

                      {location.websiteUrl && (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 flex-shrink-0" />
                          <a
                            href={location.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}>
                            {location.websiteUrl}
                          </a>
                        </div>
                      )}

                      {location.accountName && (
                        <div className="text-xs text-gray-500 mt-2">
                          Account: {location.accountName}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-end pt-4">
            <Button onClick={onSkip} variant="ghost" disabled={saving}>
              Skip for Now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
